import React from 'react';
import { Text, View } from 'react-native';
import {
  applyDriverPlanFees,
  getRateTransitions,
} from './common/utils/get-price-info';
import { ChargerAttributes } from '../models/charger-model';
import { generatePeakObj } from './common/utils/peak-off-peak-field';
import { UserPlanAssociation } from '../models/user-plan-associations-model';
import { useSelector } from 'react-redux';
import { RootState } from '../store';

const days = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
];

interface RateDisplayProps {
  renderCharger: ChargerAttributes;
}

/**
 * RateDisplay Component
 *
 * This component shows two rows:
 * - One for the Peak rate (only rendered if a peak price is provided and the schedule is valid)
 * - One for the Off-Peak rate.
 *
 * Next to each rate the extra time info is appended.
 */
const RateDisplay: React.FC<RateDisplayProps> = ({ renderCharger }) => {
  const peakData = generatePeakObj(renderCharger?.peak_selection);
  const now = new Date();
  const todayName = days[now.getDay()];
  const todaySchedule = peakData[todayName];
  const validPeakSchedule =
    todaySchedule && todaySchedule.start !== todaySchedule.end;
  const driverPlan: UserPlanAssociation | null = useSelector(
    (state: RootState) => state.user.driverPlan
  );

  const { peakTransition, offPeakTransition } =
    getRateTransitions(renderCharger);

  return (
    <View>
      {/* Render the Peak Rate row only if a peak price exists and a valid peak schedule is detected */}
      {renderCharger?.financials.price_kwh_peak > 0 && validPeakSchedule ? (
        <View className="flex flex-row justify-between items-center">
          <View style={{ flexDirection: 'row', alignItems: 'center' }}>
            <Text className="text-base font-semibold text-white">
              Peak Rate*
            </Text>
            {peakTransition ? (
              <Text
                style={{
                  color: '#39BEB7',
                  fontSize: 12,
                  marginLeft: 4,
                  fontWeight: 'bold',
                }}
              >
                {`[${peakTransition}]`}
              </Text>
            ) : null}
          </View>
          <Text className="text-base font-semibold text-white">
            {`$${(
              applyDriverPlanFees(
                renderCharger?.financials.price_kwh_peak || 0,
                driverPlan
              ) || 0
            ).toFixed(3)}/kWh`}
          </Text>
        </View>
      ) : null}

      {/* Render Off-Peak Rate row (always displayed) */}
      <View className="flex flex-row justify-between items-center">
        <View style={{ flexDirection: 'row', alignItems: 'center' }}>
          <Text className="text-base font-semibold text-white">
            {renderCharger?.financials.price_kwh_peak && validPeakSchedule
              ? 'Off-Peak Rate*'
              : 'Rate*'}
          </Text>
          {offPeakTransition ? (
            <Text
              style={{
                color: '#39BEB7',
                fontSize: 12,
                marginLeft: 4,
                fontWeight: 'bold',
              }}
            >
              {`[${offPeakTransition}]`}
            </Text>
          ) : null}
        </View>
        <Text className="text-base font-semibold text-white">
          {`$${(
            applyDriverPlanFees(
              renderCharger?.financials.price_kwh_off_peak || 0,
              driverPlan
            ) || 0
          ).toFixed(3)}/kWh`}
        </Text>
      </View>

      <View className="flex flex-row justify-between items-center">
        <View style={{ flexDirection: 'row', alignItems: 'center' }}>
          <Text
            style={{
              color: '#39BEB7',
              fontSize: 12,
              marginLeft: 4,
              fontWeight: 'bold',
            }}
          >
            *NOTE: Rates are all-inclusive with fees before taxes
          </Text>
        </View>
      </View>
    </View>
  );
};

export default RateDisplay;
